/**
 * GenericFallback renderer — for primitives without a dedicated renderer yet.
 * Shows the primitive info + a "Mark complete" button so the run can still advance.
 */
import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

export default function GenericFallback({ action, primitive, value, onChange, onNext, onBack, canBack }) {
  const params = action.params || {};
  const [ack, setAck] = useState(!!value);
  useEffect(() => { setAck(!!value); }, [action.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="bg-gradient-to-br from-slate-50 to-white border-l-4 border-slate-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
          <AlertCircle size={12} /> Coming soon · {action.type}
        </div>
        <div className="text-base text-slate-700 mb-2">
          <b>{primitive?.label || action.type}</b>{primitive?.description ? ` — ${primitive.description}` : ''}
        </div>
        {Object.keys(params).length > 0 && (
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer">Params</summary>
            <pre className="mt-1 p-2 bg-white border border-slate-200 rounded overflow-auto">{JSON.stringify(params, null, 2)}</pre>
          </details>
        )}
      </div>

      <label className="flex items-center gap-2 mb-6 p-3 border border-slate-200 rounded cursor-pointer">
        <input type="checkbox" checked={ack} onChange={e => { setAck(e.target.checked); onChange(e.target.checked); }}
          data-testid="wf-generic-ack" />
        <span className="text-sm text-slate-600">Manually acknowledged — proceed</span>
      </label>

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={!ack}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">Next →</button>
      </div>
    </div>
  );
}
